import { memoizeLru } from "./memoizeLru.js";
import { __NULL__, encodeItemIdAsExprVar, getReferencedItemIdsInExpr, } from "./references.js";
import { EXPLICIT_NULL, } from "./types.js";
function isMissingValueSignal(err) {
    return (typeof err === "object" &&
        err !== null &&
        "__missingValue" in err &&
        err.__missingValue === true);
}
function variableLookupKey(scope, key) {
    return `${scope}::${key}`;
}
/**
 * The scope a product-scoped variable lives at, derived from the item scope by a
 * prefix that cannot occur in an item id (the same collision-avoidance device
 * `evaluate.ts` uses for its synthetic non-property scope). The engine stays
 * document-agnostic: a product read is just another opaque `(scope, key)` cell.
 */
export function productVariableScope(scope) {
    return `product ${scope}`;
}
// A `prop(scope, key, fallback?)` accessor over a mutating lookup. The closure
// reads the live map, so variables resolved earlier in the list are visible to
// the ones resolved after them. Absence is two-valued:
//
//  - not in the lookup at all ⇒ *unresolvable*: throw missing, ignoring the
//    fallback, so a mistyped or stale reference cannot be defaulted into a
//    plausible value;
//  - the `null` outcome ⇒ *no value*: yield the fallback when one was supplied,
//    otherwise throw missing.
//
// A present-null (`EXPLICIT_NULL`) resolves to JS `null` without consulting the
// fallback (matching `evaluateExpr`, where a `__NULL__` property value reads back
// as `null` rather than signalling missing). A `__NULL__` fallback maps onto that
// same JS `null`, so an author can default a value-less property to a present
// null inside a larger expression; standing alone as the whole expression it is
// a bare `null` result, which `evaluateExpression` rejects (as it does for a bare
// read of a present-null property). "Not supplied" is `fallback === undefined`: `undefined` is rejected as an
// expression result and is not bound into the evaluation scope, so it is not a
// value an author can supply.
function makePropAccessor(lookup) {
    return (scope, key, fallback) => {
        const outcome = lookup.get(variableLookupKey(scope, key));
        if (outcome === undefined) {
            throw { __missingValue: true };
        }
        if (outcome === null) {
            if (fallback === undefined) {
                throw { __missingValue: true };
            }
            return fallback === __NULL__ ? null : fallback;
        }
        if (outcome.kind === "explicit_null") {
            return null;
        }
        return outcome.value;
    };
}
// A fixed variable's outcome: a present-null when its supplied value is `null`
// (the document layer states a value-less property with a *missing* variable
// rather than a `null` value, so a supplied `null` denotes an explicit present
// null), otherwise the value itself as an `ExpressionResult`.
function fixedOutcome(value) {
    switch (value.kind) {
        case "text":
            return value.value === null
                ? EXPLICIT_NULL
                : { kind: "text", value: value.value };
        case "number":
            return value.value === null
                ? EXPLICIT_NULL
                : { kind: "number", value: value.value };
        case "boolean":
            return value.value === null
                ? EXPLICIT_NULL
                : { kind: "boolean", value: value.value };
        case "number_array":
            return value.value === null
                ? EXPLICIT_NULL
                : { kind: "number_array", value: value.value };
        case "text_array":
            return value.value === null
                ? EXPLICIT_NULL
                : { kind: "text_array", value: value.value };
        case "boolean_array":
            return value.value === null
                ? EXPLICIT_NULL
                : { kind: "boolean_array", value: value.value };
        case "number_range":
            return value.value === null
                ? EXPLICIT_NULL
                : { kind: "number_range", value: value.value };
    }
}
/**
 * Resolve a list of variables in the given order and return one entry per
 * variable, positionally aligned with the input.
 *
 * The list MUST already be topologically ordered: a variable that reads another
 * (via `prop(scope, key)`) must appear after the variable that produces it. Each
 * resolved outcome is folded into a lookup so the variables resolved after it
 * read it. If the same `(scope, key)` appears more than once, the later entry
 * wins in the lookup — a plain last-writer-wins fold.
 *
 * Each entry is an {@link EvaluationOutcome}: an `ExpressionResult` (a concrete
 * value), `EXPLICIT_NULL` (a present null — the `__NULL__` sentinel or a
 * supplied `null` value), or `null` (missing — a required input was absent). A
 * computed result whose runtime type does not match its `resultDataType` raises
 * an error rather than yielding an outcome.
 */
export function resolveVariables(variables) {
    const lookup = new Map();
    const prop = makePropAccessor(lookup);
    // The product accessor is the same lookup at the product scope, so missing /
    // present-null / fallback / value handling is literally the same code path.
    const productProp = (scope, key, fallback) => prop(productVariableScope(scope), key, fallback);
    const results = [];
    for (const variable of variables) {
        const outcome = resolveOutcome(variable, prop, productProp);
        results.push(outcome);
        lookup.set(variableLookupKey(variable.ref.scope, variable.ref.key), outcome);
    }
    return results;
}
function resolveOutcome(variable, prop, productProp) {
    switch (variable.source.kind) {
        case "fixed":
            return fixedOutcome(variable.source.value);
        // A value-less variable folds into the lookup as the no-value (`null`)
        // outcome — the same outcome a computed variable produces when its own
        // inputs are missing — so both are equally fallbackable.
        case "missing":
            return null;
        case "computed":
            return evaluateExpression(variable.source.expression, variable.source.resultDataType, prop, productProp);
    }
}
function compileExpression(exprSource) {
    const referencedScopes = getReferencedItemIdsInExpr(exprSource);
    // eslint-disable-next-line no-new-func
    const fn = new Function("prop", "productProp", "__NULL__", ...referencedScopes.map(encodeItemIdAsExprVar), `return ${exprSource}`);
    return { fn, referencedScopes };
}
// The compiled function depends on the source alone, and recompiling it on
// every evaluation dominates live re-evaluation in the wizards.
const compileExpressionMemoized = memoizeLru(compileExpression, exprSource => exprSource, { max: 10_000 });
function evaluateExpression(exprSource, resultDataType, prop, productProp) {
    const { fn, referencedScopes } = compileExpressionMemoized(exprSource);
    try {
        const raw = fn(prop, productProp, __NULL__, ...referencedScopes);
        if (raw === __NULL__)
            return EXPLICIT_NULL;
        if (raw === undefined) {
            throw new Error(`undefined returned from expr: ${exprSource}`);
        }
        if (raw === null) {
            throw new Error(`null returned from expr: ${exprSource}; use __NULL__`);
        }
        return coerceToResult(raw, resultDataType, exprSource);
    }
    catch (err) {
        if (isMissingValueSignal(err))
            return null;
        throw err instanceof Error
            ? new Error(`${err.message} in expr: ${exprSource}`)
            : err;
    }
}
// Reject exactly what `evaluateExpr` rejects: a `NaN` or non-finite **number**.
// Non-number values pass untouched — the engine intentionally does NOT validate
// the result's runtime type against `resultDataType`. This matches the legacy
// evaluator, which is untyped at runtime and leaves type handling to the caller
// (`z.parse` throws / `z.safeParse` → null / a cast). Keeping the engine lenient
// here is what makes it behaviourally identical to `evaluateExpr`.
function assertNoInvalidNumber(value, exprSource) {
    if (typeof value === "number") {
        if (Number.isNaN(value)) {
            throw new Error(`NaN returned from expr: ${exprSource}`);
        }
        if (!Number.isFinite(value)) {
            throw new Error(`non-finite number returned from expr: ${exprSource}`);
        }
    }
}
function coerceToResult(raw, resultDataType, exprSource) {
    assertNoInvalidNumber(raw, exprSource);
    if (Array.isArray(raw)) {
        raw.forEach(element => assertNoInvalidNumber(element, exprSource));
    }
    // Returned as-is under the declared kind (no type validation), mirroring
    // `evaluateExpr`. The `kind` is a label; downstream reads only the value.
    return { kind: resultDataType, value: raw };
}
//# sourceMappingURL=resolveVariables.js.map